import { Injectable } from '@angular/core';
import {
  REPORT_TYPE_ICONS,
  REPORT_TYPE_TO_DEADLINE_RULE_ID,
  reportTypeForDeadlineRule,
} from '../data/report-type.catalog';
import { UpcomingDeadlineItem } from '../models/upcoming-deadline-item.model';
import { RegulatoryReportType } from 'src/app/core/models/reports';
import { DEADLINE_RULES } from './deadline-rules.catalog';
import { FundTypeFormat, UpcomingDeadlineResolved } from './deadline-rules.model';
import { getCurrentPeriodEnd, getNextPeriodEnd, differenceInCalendarDays } from './deadline-rules.utils';

@Injectable({ providedIn: 'root' })
export class UpcomingDeadlinesService {
  getForReportType(
    type: RegulatoryReportType,
    fundType?: FundTypeFormat
  ): UpcomingDeadlineItem | null {
    const rule = DEADLINE_RULES.find((r) => r.id === REPORT_TYPE_TO_DEADLINE_RULE_ID[type]);
    if (!rule) {
      return null;
    }

    const resolved = this.resolveUpcomingDeadline(rule, fundType, new Date());
    return this.toUiItem(resolved);
  }

  getUpcoming(fundType?: FundTypeFormat, count = 5): UpcomingDeadlineItem[] {
    const today = new Date();

    const resolved: UpcomingDeadlineResolved[] = DEADLINE_RULES.map((rule) =>
      this.resolveUpcomingDeadline(rule, fundType, today)
    );

    return resolved
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .slice(0, count)
      .map((r) => this.toUiItem(r));
  }

  private resolveUpcomingDeadline(
    rule: UpcomingDeadlineResolved['rule'],
    fundType: FundTypeFormat | undefined,
    today: Date
  ): UpcomingDeadlineResolved {
    let periodEnd = getCurrentPeriodEnd(rule.frequency);
    let deadline = rule.computeDeadline(periodEnd, fundType);
    let daysLeft = differenceInCalendarDays(deadline, today);

    while (daysLeft < 0) {
      periodEnd = getNextPeriodEnd(rule.frequency, periodEnd);
      deadline = rule.computeDeadline(periodEnd, fundType);
      daysLeft = differenceInCalendarDays(deadline, today);
    }

    const isFoF =
      fundType === FundTypeFormat.FundOfFunds &&
      (rule.id === 'bafin-annex-iv' || rule.id === 'quarterly-lp-report');

    return { rule, periodEnd, deadline, daysLeft, isFoF };
  }

  private toUiItem(r: UpcomingDeadlineResolved): UpcomingDeadlineItem {
    const deadlineStr = r.deadline.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const variant = reportTypeForDeadlineRule(r.rule.id);
    const icons = REPORT_TYPE_ICONS[variant];

    return {
      id: `${r.rule.id}-${r.periodEnd.getFullYear()}-${r.periodEnd.getMonth()}`,
      icon: icons.material,
      iconSrc: icons.svg,
      title: r.rule.reportName,
      subtitle: `${r.rule.regulator} · Deadline: ${deadlineStr}`,
      variant,
      isFoF: r.isFoF,
      deadline: r.deadline,
    };
  }
}
